import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  ToolbarDivider,
  Tooltip,
} from '@fluentui/react-components';
import {
  AddRegular,
  BookDatabase20Regular,
  BracesRegular,
  DocumentLinkRegular,
  DocumentPageBreakRegular,
  Library20Regular,
  MathFormulaRegular,
  LinkRegular,
} from '@fluentui/react-icons';

import ToolbarButton from './toolbar/ToolbarButton';
import { ETiptapSelectorKey } from '../../types/tiptap.types';
import LandscapeSectionButton from './toolbar/LandscapeSectionButton';
import ImageNodeButton from '@modules/documents/tiptap/nodes/image/ImageNodeButton';
import LinkMenuContext from '../../contexts/MenuContext.context';
import TableNodeButton from '@modules/documents/tiptap/nodes/table/TableNodeButton';
import { TSelectItemDialogState, TSelectVersionDialogState } from '@modules/items';
import { AccessType } from '@modules/access';
import { TBlockItemType, TSelectCaptionDialogState, TSelectHeadingDialogState } from '../../types';
import { Overflow, TOverflowNodeParam, TSingleFieldDialogState } from '@shared/components';
import { TOptionsDialogState, type TIcon } from '@shared/types';
import DocumentContext from '../../contexts/DocumentContext.context';
import { TERMINOLOGY } from '@shared/i18n';
import { GlobalLibraryRegular, LibraryLinkRegular } from '@shared/icons';
import { SignInContext } from '@modules/users';
import { TInsertLiteratureCitationDialogState, TSelectBlockDialogState } from '../../types/internal.types';
import useInsertReference from '../../hooks/useInsertReference.hook';
import useInsertBlock from '../../hooks/useInsertBlock.hook';
import useInsertLiteratureCitation from '../../hooks/useInsertLiteratureCitation.hook';
import useCreateBlock from '../../hooks/useCreateBlock.hook';
import useEditorActive from '../../hooks/useEditorActive.hook';
import useToolbarButtonStyles from '../../hooks/useToolbarButtonStyles.hook';
import { useMenuButtonStyles } from '@shared/hooks';
import inferSelectionKind from '../../helpers/inferSelectionKind.helper';
import { TMathematicsDialogState } from '../../types';
import MathematicsDialog from '../dialogs/MathematicsDialog';
import useTemplateFieldInsert from '../../hooks/useTemplateFieldInsert.hook';
import { isSelectionInField } from '../../tiptap/extensions/TemplateLocking';
import { TEMPLATE_FIELD_BLOCK_NAME, TEMPLATE_FIELD_INLINE_NAME } from '../../doc-engine/templateFields';
import {
  selectionHasTemplateField,
  checkBlockFieldIncompatible,
  checkInlineFieldIncompatible,
} from '../../helpers/templateFieldValidation.helper';
import {
  selectionHasUnsupportedBlockNode,
  UNSUPPORTED_BLOCK_CONTENT_MESSAGE,
} from '../../helpers/blockContentValidation.helper';
import getInsertBlocks, { type TInsertBlockConfig } from '../../helpers/getInsertBlocks.helper';
import { usesFormFields } from '../../helpers/formFields.helper';

type TProps = {
  setSelectItemDialogState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
  setSelectVersionDialogState: Dispatch<SetStateAction<TSelectVersionDialogState | undefined>>;
  setSelectHeadingDialogState: Dispatch<SetStateAction<TSelectHeadingDialogState | undefined>>;
  setSelectCaptionDialogState: Dispatch<SetStateAction<TSelectCaptionDialogState | undefined>>;
  setOptionsDialogState: Dispatch<SetStateAction<TOptionsDialogState | undefined>>;
  setSelectBlockDialogState: Dispatch<SetStateAction<TSelectBlockDialogState | undefined>>;
  setSelectBlockTargetDialogState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
  setSingleFieldDialogState: Dispatch<SetStateAction<TSingleFieldDialogState<'input'> | undefined>>;
  setInsertLiteratureCitationDialogState: Dispatch<SetStateAction<TInsertLiteratureCitationDialogState | undefined>>;

  setSelectionKind: Dispatch<SetStateAction<TBlockItemType | null>>;
};

const renderInsertBlockIcon = (iconVariant: TInsertBlockConfig['iconVariant']): TIcon => {
  switch (iconVariant) {
    case 'qm':
      return (
        <div style={{ fontSize: 18 }}>
          <GlobalLibraryRegular />
        </div>
      );
    case 'url':
      return <LibraryLinkRegular className="mt-px" />;
    case 'default':
      return <Library20Regular className="mt-px" />;
  }
};

const Insert = ({
  setSelectItemDialogState,
  setSelectVersionDialogState,
  setSelectHeadingDialogState,
  setSelectCaptionDialogState,
  setOptionsDialogState,
  setSelectBlockDialogState,
  setSelectBlockTargetDialogState,
  setSingleFieldDialogState,
  setInsertLiteratureCitationDialogState,

  setSelectionKind,
}: TProps) => {
  const { open: openLinkMenu } = useContext(LinkMenuContext);
  const { doc, editor, templateDropdownOptionsRef } = useContext(DocumentContext);
  const { handleInsertField, handleInsertDropdownField } = useTemplateFieldInsert(editor, templateDropdownOptionsRef);
  const [mathematicsDialogState, setMathematicsDialogState] = useState<TMathematicsDialogState | undefined>(undefined);
  const { organization, folderAccess } = useContext(SignInContext);

  const {
    selectionKind,
    hasUnsupportedBlockNode,
    isInsideField,
    isBlockFieldIncompatible,
    hasIncompatibleInlineContent,
    isTextOnlyContext,
  } = useEditorActive((e) => {
    const newSelectionKind = inferSelectionKind(e);

    // Selection (or empty caret) is in a textblock with content:'text*' (title, caption,
    // heading) - no template fields allowed there.
    const { $from, $to } = e.state.selection;
    const inlineFieldType = e.schema.nodes[TEMPLATE_FIELD_INLINE_NAME];
    const textOnly =
      $from.sameParent($to) &&
      $from.parent.isTextblock &&
      !!inlineFieldType &&
      !$from.parent.type.contentMatch.matchType(inlineFieldType);

    return {
      selectionKind: newSelectionKind,
      hasUnsupportedBlockNode: selectionHasUnsupportedBlockNode(e),
      isInsideField: isSelectionInField(e.state),
      isBlockFieldIncompatible: checkBlockFieldIncompatible(e),
      hasIncompatibleInlineContent: checkInlineFieldIncompatible(e),
      isTextOnlyContext: textOnly,
    };
  });

  useEffect(() => {
    setSelectionKind(selectionKind);
  }, [selectionKind, setSelectionKind]);

  const handleInsertReference = useInsertReference({
    setSelectItemDialogState,
    setSelectVersionDialogState,
    setSelectHeadingDialogState,
    setSelectCaptionDialogState,
    setOptionsDialogState,
  });

  const handleInsertBlock = useInsertBlock({
    setSelectBlockDialogState,
    setSelectVersionDialogState,
  });

  const handleInsertLiteratureCitation = useInsertLiteratureCitation({
    setInsertLiteratureCitationDialogState,
  });

  const isLiteratureFeatureEnabled =
    !!organization.featureSettings?.literatures?.enabled && !!organization.folders.literature;

  const handleCreateBlock = useCreateBlock({ setSelectBlockTargetDialogState, setSingleFieldDialogState });
  const blockTargetRoot = doc.blocksRoot ?? doc.insertBlocks.globalBlocksRoot;

  const handleCreateIdReference = useCallback(() => {
    editor.chain().focus().toggleHeading({ level: 5 }).run();
  }, []);

  const handleCreatePageBreak = useCallback(() => {
    editor.chain().focus().insertPageBreak().run();
  }, []);

  // Reuse ToolbarButton styles for Overflow menu button
  const classes = useToolbarButtonStyles();
  const menuButtonClasses = useMenuButtonStyles();

  const params: TOverflowNodeParam[] = useMemo(() => {
    const list: TOverflowNodeParam[] = [];

    // Template field insertion (only for templates with templateBinding enabled); form fields come from Home's Add field
    if (doc.isTemplate && organization.featureSettings.templateBinding?.enabled && !usesFormFields(doc)) {
      list.push({
        node: (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Tooltip content="Insert a template field" relationship="label" withArrow>
                <MenuButton
                  className={mergeClasses(classes.root, classes.reducePadding, menuButtonClasses.menuButton)}
                  icon={<BracesRegular />}
                >
                  Field
                </MenuButton>
              </Tooltip>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  disabled={
                    selectionKind === 'DOCUMENT' || isInsideField || hasIncompatibleInlineContent || isTextOnlyContext
                  }
                  onClick={() => handleInsertField(true)}
                >
                  Inline field
                </MenuItem>
                <MenuItem
                  disabled={isInsideField || isBlockFieldIncompatible || isTextOnlyContext}
                  onClick={() => handleInsertField(false)}
                >
                  Content field
                </MenuItem>
                <MenuItem
                  disabled={
                    selectionKind === 'DOCUMENT' || isInsideField || hasIncompatibleInlineContent || isTextOnlyContext
                  }
                  onClick={handleInsertDropdownField}
                >
                  Dropdown field
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ),
      });
    }

    // Table
    list.push({ node: <TableNodeButton /> });

    // Image
    list.push({ node: <ImageNodeButton /> });

    // URL
    list.push({
      node: (
        <ToolbarButton
          icon={
            <div style={{ fontSize: 22 }}>
              <LinkRegular />
            </div>
          }
          tooltip="Insert URL"
          // Headings allow no marks, so the link mark would be silently stripped.
          disabled={(e) => e.isActive(ETiptapSelectorKey.HEADING)}
          onClick={() => openLinkMenu()}
        >
          URL
        </ToolbarButton>
      ),
    });

    list.push({
      node: (
        <ToolbarButton
          icon={
            <div style={{ fontSize: 24 }}>
              <DocumentPageBreakRegular />
            </div>
          }
          // Template fields exclude page breaks by schema and guard.
          disabled={(e) => e.isActive(TEMPLATE_FIELD_BLOCK_NAME) || e.isActive(TEMPLATE_FIELD_INLINE_NAME)}
          onClick={handleCreatePageBreak}
        >
          Page break
        </ToolbarButton>
      ),
    });

    list.push({
      node: (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content="More actions" relationship="label" withArrow>
              <MenuButton className={mergeClasses(classes.root, classes.reducePadding, menuButtonClasses.menuButton)}>
                More
              </MenuButton>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover className={menuButtonClasses.menuPopover}>
            <MenuList>
              <LandscapeSectionButton />
              <ToolbarButton
                icon={
                  <div style={{ fontSize: 21 }}>
                    <MathFormulaRegular />
                  </div>
                }
                tooltip="Insert math equation"
                onClick={() => setMathematicsDialogState({ isOpen: true })}
              >
                Equation
              </ToolbarButton>
            </MenuList>
          </MenuPopover>
        </Menu>
      ),
    });

    // Divider
    list.push({ node: <ToolbarDivider style={{ marginTop: '6px' }} /> });

    // Conditional actions
    if (!doc.isBlock) {
      // Reference
      list.push({
        node: (
          <ToolbarButton
            icon={
              <div style={{ fontSize: 22, marginTop: -2 }}>
                <DocumentLinkRegular />
              </div>
            }
            tooltip={`Insert ${TERMINOLOGY.reference.lower}`}
            // Headings are text-only; the inline reference atom cannot be inserted there.
            disabled={(e) => e.isActive(ETiptapSelectorKey.HEADING)}
            onClick={handleInsertReference}
          >
            {TERMINOLOGY.reference.upper}
          </ToolbarButton>
        ),
      });

      // Literature citation (feature-flag gated: flag + literatureFolder)
      if (isLiteratureFeatureEnabled) {
        const hasLiteratureAccess = folderAccess.literature >= AccessType.READ;
        list.push({
          node: (
            <ToolbarButton
              icon={<BookDatabase20Regular className="mt-px" />}
              tooltip={
                hasLiteratureAccess
                  ? `Insert ${TERMINOLOGY.literature.lower} citation`
                  : `You don't have access rights for ${TERMINOLOGY.literature.lower}.`
              }
              disabled={(e) => !hasLiteratureAccess || e.isActive(ETiptapSelectorKey.HEADING)}
              onClick={handleInsertLiteratureCitation}
            >
              Citation
            </ToolbarButton>
          ),
        });
      }
    }

    const getLibraryTooltip = (library: { lower: string; upper: string }, blocksRoot?: string) => {
      if (doc.isBlock) return `${TERMINOLOGY.block.upper} cannot contain other ${TERMINOLOGY.blockEither.lower} yet.`;
      if (blocksRoot) return `Insert ${TERMINOLOGY.blockEither.lower} from ${library.lower}.`;
      return `You don't have access rights for ${library.lower}.`;
    };
    const getIsLibraryDisabled = (blocksRoot?: string) => () => !blocksRoot || doc.isBlock;

    getInsertBlocks(doc, organization).forEach(({ key, blocksRoot, iconVariant, label, show, style }) => {
      if (!show) return;

      list.push({
        node: (
          <ToolbarButton
            key={key}
            icon={renderInsertBlockIcon(iconVariant)}
            tooltip={getLibraryTooltip(label, blocksRoot)}
            disabled={getIsLibraryDisabled(blocksRoot)}
            onClick={() => handleInsertBlock(blocksRoot)}
            style={style}
          >
            {label.upper}
          </ToolbarButton>
        ),
      });
    });

    // ID Reference (feature flag)
    if (organization.mock.isH5RefEnabled && !doc.isBlock) {
      list.push({
        node: (
          <ToolbarButton
            icon={
              <div style={{ fontSize: 21, marginRight: -3 }}>
                <AddRegular />
              </div>
            }
            tooltip={`Create ID ${TERMINOLOGY.reference.lower} for the selected content.`}
            // Creates an h5 heading; template fields exclude headings by schema.
            disabled={(e) => e.isActive(TEMPLATE_FIELD_BLOCK_NAME) || e.isActive(TEMPLATE_FIELD_INLINE_NAME)}
            onClick={handleCreateIdReference}
            style={{ paddingLeft: '2px' }}
          >
            ID {TERMINOLOGY.reference.upper}
          </ToolbarButton>
        ),
      });
    }

    // A Change Template document's content stays in its template.
    if (selectionKind && doc.parentType !== 'CHANGE_TEMPLATE') {
      const getCreateBlockTooltip = () => {
        if (!blockTargetRoot)
          return `No ${TERMINOLOGY.productDatabase.lower} or ${TERMINOLOGY.qmDatabase.lower} is available for this document.`;
        if (hasUnsupportedBlockNode) return UNSUPPORTED_BLOCK_CONTENT_MESSAGE;
        return `Create or update ${selectionKind === 'INLINE_BLOCK' ? TERMINOLOGY.inlineBlock.lower : TERMINOLOGY.block.lower} with the selected ${selectionKind === 'INLINE_BLOCK' ? 'text' : 'content'}.`;
      };

      list.push({
        node: (
          <ToolbarButton
            icon={
              <div style={{ fontSize: 21, marginRight: -3 }}>
                <AddRegular />
              </div>
            }
            tooltip={getCreateBlockTooltip()}
            // Creates or updates another item from the selection without editing this document.
            worksWhenLocked
            disabled={(e) => !blockTargetRoot || hasUnsupportedBlockNode || selectionHasTemplateField(e)}
            onClick={handleCreateBlock}
            style={{ paddingLeft: '2px' }}
          >
            {selectionKind === 'INLINE_BLOCK' ? TERMINOLOGY.inlineBlock.upper : TERMINOLOGY.block.upper}
          </ToolbarButton>
        ),
      });
    }

    return list;
  }, [
    openLinkMenu,
    doc.isBlock,
    doc.isTemplate,
    doc.parentType,
    doc.path,
    doc.insertBlocks,
    blockTargetRoot,
    organization.mock.isH5RefEnabled,
    organization.folders.countries,
    organization.folders.qm,
    organization.featureSettings.templateBinding?.enabled,
    isLiteratureFeatureEnabled,
    folderAccess.literature,
    selectionKind,
    hasUnsupportedBlockNode,
    isInsideField,
    isBlockFieldIncompatible,
    hasIncompatibleInlineContent,
    isTextOnlyContext,
    handleInsertReference,
    handleCreateIdReference,
    handleInsertBlock,
    handleInsertLiteratureCitation,
    handleCreateBlock,
    handleCreatePageBreak,
    handleInsertField,
    handleInsertDropdownField,
  ]);

  return (
    <div className="w-full">
      <MathematicsDialog state={mathematicsDialogState} setState={setMathematicsDialogState} />
      <Overflow
        params={params}
        defaultAppearance="secondary"
        label="More"
        menuButtonClassname={mergeClasses(classes.root, classes.reducePadding)}
      />
    </div>
  );
};

export default Insert;
